import { useCallback, useState } from "react";

interface Args<TRow extends { id: string }> {
    rows: TRow[];
    onEditingChange?: (isEditing: boolean) => void;
}

export function useCounterpartyBookEditor<TRow extends { id: string }>({
    rows,
    onEditingChange,
}: Args<TRow>) {
    const [isAdding, setIsAdding] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [deletingRow, setDeletingRow] = useState<TRow | null>(null);

    const editingRow = isAdding
        ? undefined
        : rows.find((row) => row.id === editingId);
    const isModalOpen = isAdding || editingId !== null;

    const closeModal = useCallback(() => {
        setIsAdding(false);
        setEditingId(null);
        onEditingChange?.(false);
    }, [onEditingChange]);

    const startAdd = useCallback(() => {
        setEditingId(null);
        setIsAdding(true);
        onEditingChange?.(true);
    }, [onEditingChange]);

    const startEdit = useCallback(
        (id: string) => {
            setIsAdding(false);
            setEditingId(id);
            onEditingChange?.(true);
        },
        [onEditingChange],
    );

    const startDelete = useCallback(
        (id: string) => {
            const row = rows.find((item) => item.id === id);
            if (row) {
                setDeletingRow(row);
            }
        },
        [rows],
    );

    const clearDeleting = useCallback(() => {
        setDeletingRow(null);
    }, []);

    return {
        editingRow,
        isModalOpen,
        deletingRow,
        closeModal,
        startAdd,
        startEdit,
        startDelete,
        clearDeleting,
    };
}
